<template>
  <div class="m-cart__product-total" v-if="!loading">
    <transition-group class="m-cart__products" name="cart-list" tag="ul" aria-label="cart products">
      <li class="m-cart__product" v-for="product in cartStore.cart.products" :key="product.ID">
        <div class="m-cart__product-img">
          <!-- @TODO Fix images with correct sources padding-ratio alt and stuff -->
          <picture class="a-image a-image--contain" style="--padding-ratio: calc(1 / 1);" v-if="product.image_url !== ''">
            <img class="a-image__img"
              width="100" height="100"
              :src="product.image_url + '?width=5&height=5'"
              :data-src="product.image_url + '?width=100&height=100'"
              :alt="product.name" loading="lazy"/>
          </picture>
        </div>
        <div class="m-cart__product-right">
          <div class="m-cart__product-info">
            <a href="#" class="m-cart__product-title">
              {{ product.name }}
            </a>
            <span class="m-cart__product-size" v-for="[name, attributeValue] of Object.entries(product.attributes)">
              {{ attributeValue.name }}: {{ attributeValue.value }}
            </span>
          </div>
          <div class="m-cart__product-actions">
            <input class="m-cart__product-amount a-input a-input--amount" type="number" v-model="newCartQuantity[product.ID]">
            <button class="a-btn m-cart__product-delete" type="button" v-if="(newCartQuantity[product.ID] ?? 0) !== product.quantity" @click="changeValue(product)">
              {{ t('cart.button.save') }}
            </button>
            <button class="a-btn m-cart__product-delete" type="button" @click="remove(product.ID)">
              {{ t('cart.button.delete') }}
            </button>
          </div>
          <div class="m-cart__product-prices">
            <span class="m-cart__product-price m-cart__product-price--old" v-if="product.price < product.original_price">
              {{ displayPrice(product.original_price) }}
            </span>
            <span class="m-cart__product-price" :class="{ 'm-cart__product-price--sale': product.price < product.original_price }">
              {{ displayPrice(product.price) }}
            </span>
          </div>
        </div>
      </li>
    </transition-group>
    <transition v-if="!loading">
      <div class="m-cart__empty" v-if="cartStore.cart.products.length <= 0">
        <span>
          {{ translate('cart.empty.text') }}
        </span>
      </div>
    </transition>

    <div class="m-cart__bottom" v-if="cartStore.cart.products.length > 0">
      <div class="m-cart__coupon">
        <span>
          {{ t('cart.coupon.title') }}
        </span>
        <base-coupon />
      </div>
      <div class="m-cart__prices">
        <span class="m-cart__price" v-if="cartStore.cart.postnl_data !== null && (cartStore.cart.postnl_data?.price ?? 0) > 0">
          {{ t('cart.postnl.title') }}
          <span>
            {{ displayPrice(cartStore.cart.postnl_data?.price ?? 0, t('checkout.price.free')) }}
          </span>
        </span>
        <span class="m-cart__price">
          {{ cartStore.cart.checkout_method?.name }}
          <span>
            {{ displayPrice(cartStore.cart.checkout_method?.price ?? 0, t('checkout.price.free')) }}
          </span>
        </span>

        <span class="m-cart__price" v-if="cartStore.cart.coupon !== null">
          {{ t('cart.general.coupon_price.label', { code: cartStore.cart.coupon?.code }) }}
          <span>
            {{
              displayPrice((cartStore.cart.coupon?.discount ?? 0) * -1) }}
          </span>
        </span>
        <span class="m-cart__price">
          {{ t('cart.general.price_total') }}
          <span>
            {{ displayPrice(cartStore.cart.total ?? 0, t('checkout.price.free')) }}
          </span>
        </span>
      </div>
    </div>
    <div class="m-cart__wrapping" v-if="cartStore.cart.products.length > 0 && cartStore.giftWrapItems.length > 0">
      <span class="m-cart__wrapping-text">
        {{ t('cart.gift_wrap.description') }}
      </span>
      <fieldset class="m-cart__wrapping-fieldset">
        <legend class="u-screen-reader-only">
          {{ t('cart.gift_wrap.description') }}
        </legend>
        <div class="a-checkbox a-checkbox--wrapping" v-for="giftWrapItem of cartStore.giftWrapItems">
          <input class="a-checkbox__input" type="checkbox" :name="giftWrapItem.name" :value="giftWrapItem.ID" :id="giftWrapItem.ID" :checked="cartStore.cart.wrapping_paper?.ID === giftWrapItem.ID" @click="toggleWrappingPaper(giftWrapItem.ID)" />
          <label class="a-checkbox__box" :for="giftWrapItem.ID">
            <!-- @TODO Fix images with correct sources padding-ratio alt and stuff -->
            <picture class="a-image a-image--contain" style="--padding-bottom: calc(1/1)">
              <img class="a-image__image"
                :src="giftWrapItem.image_url + '?width=150&height=140'"
                :alt="giftWrapItem.name" />
            </picture>
            <span class="a-checkbox__label">
              {{ giftWrapItem.name }}
            </span>
            <span class="a-checkbox__label">
              {{ displayPrice(giftWrapItem.price, t('checkout.price.free')) }}
            </span>
          </label>
        </div>
      </fieldset>
      <div class="m-cart__wrapping-overview" v-if="cartStore.cart.wrapping_paper">
        {{ t('cart.gift_wrap.selected_description') }} <strong>{{ cartStore.cart.wrapping_paper.name }}</strong>
      </div>
    </div>
  </div>
  <div v-else class="m-cart__product-total">
    <div class="m-cart__products">
      <div class="m-cart__product m-cart__product--skeleton">
        <div class="m-cart__product-img">
        </div>
        <div class="m-cart__product-right">
          <div class="m-cart__product-info">
            <span class="m-cart__product-title"></span>
            <span class="m-cart__product-size"></span>
          </div>
          <div class="m-cart__product-actions">
            <span class="m-cart__product-amount a-input a-input--amount"></span>
          </div>
          <div class="m-cart__product-prices">
            <span class="m-cart__product-price"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Ref, ref, onMounted, watch, ComputedRef, computed, nextTick } from 'vue'
import { useCartStore } from '../../stores/cart'
import { displayPrice } from '../../helper/currency'
import BaseCoupon from './BaseCoupon.vue'
import { useI18n } from 'vue-i18n'
import { translate } from '../../helper-functions'
import { lazyLoadUpdate } from '../../lazyload'

const { t } = useI18n()
const cartStore = useCartStore()
const componentLoading: Ref<boolean> = ref(true)
const loading: ComputedRef<Boolean> = computed(() => cartStore.loadingCart || componentLoading.value)

let newCartQuantity: Ref<Record<string, number>> = ref({})

onMounted(async () => {
  await cartStore.init()
  componentLoading.value = false
  nextTick(()=>{
    lazyLoadUpdate()
  })
})

watch(() => cartStore.cart, async () => {
  await cartStore.init()
  // Set or reset the new quantity for the current cart
  for (let product of cartStore.cart.products) {
    newCartQuantity.value[product.ID] = product.quantity
  }

  nextTick(()=>{
    lazyLoadUpdate()
  })
})

async function toggleWrappingPaper(ID: string) {
  if (cartStore.cart.wrapping_paper?.ID === ID) {
    await cartStore.removeWrappingPaper()
    return
  }

  cartStore.setWrappingPaper(ID)
}

async function changeValue(product: { ID: string, quantity: number, configuration_of: string|null }) {
  componentLoading.value = true
  const newQuantity = newCartQuantity.value[product.ID] ?? 0
  if (newQuantity === product.quantity) {
    return
  }

  if (product.quantity < newQuantity) {
    await cartStore.add(product.ID, newQuantity - product.quantity, product.configuration_of)
  } else if (product.quantity > newQuantity) {
    await cartStore.remove(product.ID, product.quantity - newQuantity)
  }

  componentLoading.value = false
}

async function remove(productID: string) {
  componentLoading.value = true
  const quantity = newCartQuantity.value[productID] ?? 0
  if (quantity === 0) {
    return
  }

  await cartStore.remove(productID, quantity)
  componentLoading.value = false
}

</script>
